import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { HttpService } from './http.service';
import { DataSharingService } from './data-sharing.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private readonly GENERAL_KEY = 'general_settings';
  private readonly SPLASH_KEY = 'splash_screen_settings';
  private readonly SHIPPING_KEY = 'shipping_settings';
  private readonly INVOICING_KEY = 'invoicing_settings';

  private generalSettings$: Observable<any> | null = null;
  private splashSettings$: Observable<any> | null = null;
  private shippingSettings$: Observable<any> | null = null;
  private invoicingSettings$: Observable<any> | null = null;

  constructor(
    private http: HttpService,
    private dataSharing: DataSharingService
  ) {}

  // ============================================================
  // GENERAL
  // ============================================================

  loadGeneralSettings(forceReload = false): Observable<any> {
    if (this.generalSettings$ && !forceReload) {
      return this.generalSettings$;
    }

    this.generalSettings$ = this.http.sendGET_OneSettingsApp('General').pipe(
      tap((res) => {
        this.handleVersionCheck(res);
        this.storeGeneralInLocalStorage(res);
        this.emitGeneralToDataSharing(res);
      }),
      shareReplay(1)
    );

    return this.generalSettings$;
  }

  getGeneralSettings(): any {
    const raw = localStorage.getItem(this.GENERAL_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  getGeneralSetting<T = any>(key: string, defaultValue: T): T {
    const obj = this.getGeneralSettings();
    return obj?.[key] ?? defaultValue;
  }

  private handleVersionCheck(res: any) {
    const lastVersion = localStorage.getItem('version_admin_web') || '';
    const currentVersion = res?.version_admin_web || '';

    if (currentVersion && currentVersion !== lastVersion) {
      localStorage.setItem('version_admin_web', currentVersion);
      location.reload();
    }
  }

  private storeGeneralInLocalStorage(s: any) {
    localStorage.setItem(this.GENERAL_KEY, JSON.stringify(s || {}));
  }

  private emitGeneralToDataSharing(s: any) {
    const tx = s?.transactions || {};

    this.dataSharing.currentMoneySubject.next(s?.currentMoney || '');
    this.dataSharing.currentTaxSubject.next(tx['tax'] || 0);
    this.dataSharing.appName.next(s?.appName || '');
    this.dataSharing.alert_new_order_sound.next(s?.alert_new_order_sound || false);
    this.dataSharing.primary_color.next(s?.ionic_color?.ion_color_base || '');
    this.dataSharing.secondary_color.next(s?.ionic_color_second?.ion_color_base || '');
    this.dataSharing.isRestaurant.next(s?.isRestaurant || false);
    this.dataSharing.isPointOfSale.next(s?.isPointOfSale || false);
    this.dataSharing.hasTablesReservation.next(s?.hasTablesReservation || false);
    this.dataSharing.hasNumberOfCustomers.next(s?.hasNumberOfCustomers || false);
    this.dataSharing.isAdminMemberShip.next(s?.isAdminMemberShip || false);
    this.dataSharing.isOnlyView.next(s?.isOnlyView || false);
    this.dataSharing.isRetail.next(s?.isRetail || false);
    this.dataSharing.isProjectService.next(s?.isProjectService || false);
    this.dataSharing.show_supplier.next(s?.show_supplier || false);
    this.dataSharing.unique_tax.next(tx['unique_tax'] || false);
    this.dataSharing.transactions.next(tx);
  }

  // ============================================================
  // SPLASH SCREEN
  // ============================================================

  loadSplashSettings(forceReload = false): Observable<any> {
    if (this.splashSettings$ && !forceReload) {
      return this.splashSettings$;
    }

    this.splashSettings$ = this.http.sendGET_OneSettingsApp('SplashScreen').pipe(
      tap((res) => {
        localStorage.setItem(this.SPLASH_KEY, JSON.stringify(res || {}));
      }),
      shareReplay(1)
    );

    return this.splashSettings$;
  }

  getSplashSettings(): any {
    const raw = localStorage.getItem(this.SPLASH_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  getSplashSetting<T = any>(key: string, defaultValue: T): T {
    const obj = this.getSplashSettings();
    return obj?.[key] ?? defaultValue;
  }

  // ============================================================
  // SHIPPING
  // ============================================================

  loadShippingSettings(forceReload = false): Observable<any> {
    if (this.shippingSettings$ && !forceReload) {
      return this.shippingSettings$;
    }

    this.shippingSettings$ = this.http.sendGET_OneSettingsApp('Shipping').pipe(
      tap((res) => {
        localStorage.setItem(this.SHIPPING_KEY, JSON.stringify(res || {}));
      }),
      shareReplay(1)
    );

    return this.shippingSettings$;
  }

  getShippingSettings(): any {
    const raw = localStorage.getItem(this.SHIPPING_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  getShippingSetting<T = any>(key: string, defaultValue: T): T {
    const obj = this.getShippingSettings();
    return obj?.[key] ?? defaultValue;
  }

  // ============================================================
  // INVOICING
  // ============================================================

  loadInvoicingSettings(forceReload = false): Observable<any> {
    if (this.invoicingSettings$ && !forceReload) {
      return this.invoicingSettings$;
    }

    this.invoicingSettings$ = this.http.sendGET_OneSettingsApp('Invoicing').pipe(
      tap((res) => {
        localStorage.setItem(this.INVOICING_KEY, JSON.stringify(res || {}));
      }),
      shareReplay(1)
    );

    return this.invoicingSettings$;
  }

  getInvoicingSettings(): any {
    const raw = localStorage.getItem(this.INVOICING_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  getInvoicingSetting<T = any>(key: string, defaultValue: T): T {
    const obj = this.getInvoicingSettings();
    return obj?.[key] ?? defaultValue;
  }
}